<template>
  <ValidationObserver v-slot="v" slim>
    <b-form class="reset-password-form" @submit.prevent="v.handleSubmit(resetPassword)">
      <b-row>
        <img class="logo" :src="themeImage" alt="Company Logo" />
      </b-row>
      <sme-alert v-if="error" level="danger" class="mb-4">{{ error }}</sme-alert>
      <sme-alert v-if="linkExpired" level="danger" class="mb-4"
        >This link has expired or has already been used.
        <router-link :to="{ name: 'forgot-password' }">Request a new one</router-link>.</sme-alert
      >
      <sme-alert v-if="success" level="good" class="mb-4"
        >Your password has been set. Taking you to the login page…</sme-alert
      >
      <b-row>
        <b-col>
          <app-input
            v-model="password"
            :validate-immediate="false"
            label="Password"
            name="password"
            description="Must be at least 12 characters with a capital letter, number and special character"
            rules="password"
            type="password"
            mask-valid
          />
          <app-input
            v-model="confirmPassword"
            :validate-immediate="false"
            label="Confirm Password"
            name="confirm-password"
            rules="required|confirmed:password"
            type="password"
            mask-valid
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button type="submit" class="submit" variant="primary" :disabled="v.invalid || loading">Submit</b-button>
          <app-loading :loading="loading" />
        </b-col>
      </b-row>
    </b-form>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import ApiClient from '@/ApiClient';

export default {
  name: 'SetPassword',
  components: {
    ValidationObserver,
    AppInput,
    AppLoading,
    SmeAlert,
  },
  props: {
    themeImage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      password: '',
      confirmPassword: '',
      loading: false,
      error: undefined,
      linkExpired: false,
      success: false,
    };
  },
  methods: {
    async resetPassword() {
      this.loading = true;
      this.error = undefined;
      this.linkExpired = false;
      let response;
      try {
        response = await ApiClient.resetPassword(
          this.password,
          this.$route.query.reset_id,
          this.$route.query.reset_token,
        );
      } catch (e) {
        response = e.response;
      }
      const data = response?.data ?? {};
      if (data.is_valid) {
        this.success = true;
        // set a timeout to make it more obvious that the password has been reset
        setTimeout(() => {
          this.$router.push({ name: 'login' });
        }, 2000);
      } else if (data.password_issues || data.message) {
        // the API rejected the password itself (411 or admin password rules)
        this.error = data.message || 'That password does not meet the requirements.';
        this.loading = false;
      } else if (response?.status === 200) {
        // the API answered but found no usable token: expired, already used or tampered
        this.linkExpired = true;
        this.loading = false;
      } else {
        // no response or a server error: the link may still be fine, so let them retry
        this.error = 'Unable to reset your password. Please try again.';
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.logo {
  object-fit: contain;
  margin-bottom: 36px;
  height: 64px;
  width: 100%;
}

.submit {
  margin-top: 24px;
  padding: 12px 16px;
  width: 100%;
}
</style>
